import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiResendEmailSubmitOutput,
  type EnrollmentMultiResendEmailSubmitInput,
  type EnrollmentMultiVerifyEmailData,
  type EnrollmentMultiVerifyEmailSubmitInput,
  type EnrollmentMultiVerifyEmailSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi VerifyEmail data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiVerifyEmailData>
 * @example const response = await getEnrollmentMultiVerifyEmailData()
 */
export const getEnrollmentMultiVerifyEmailData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiVerifyEmailData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiVerifyEmailData',
      query: `mutation getEnrollmentMultiVerifyEmailData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              email
              progress
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiVerifyEmailData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiVerifyEmailData = createQueryHook<undefined, EnrollmentMultiVerifyEmailData>(
  getEnrollmentMultiVerifyEmailData,
)

/**
 * Submit Enrollment Multi VerifyEmail data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiVerifyEmailSubmitOutput>
 * @example const response = await submitEnrollmentMultiVerifyEmailData();
 */
export const submitEnrollmentMultiVerifyEmailData = async (
  enrollmentVerifyEmailSubmitInput: EnrollmentMultiVerifyEmailSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiVerifyEmailSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiVerifyEmailData',
      query: `mutation submitEnrollmentMultiVerifyEmailData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              email
              applicationId
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
            type
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentVerifyEmailSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiVerifyEmailSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiVerifyEmailData = createMutationHook<
  EnrollmentMultiVerifyEmailSubmitInput,
  EnrollmentMultiVerifyEmailSubmitOutput
>(submitEnrollmentMultiVerifyEmailData)

/**
 * Resend  email - VerifyEmail data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSResendEmailSubmitOutput>
 * @example const response = await submitEnrollmentHYCSVerifyEmailData();
 */
export const submitEnrollmentMultiResendEmailData = async (
  enrollmentResendEmailSubmitInput: EnrollmentMultiResendEmailSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiResendEmailSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiResendEmailData',
      query: `mutation submitEnrollmentMultiResendEmailData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              email
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
            type
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentResendEmailSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiResendEmailSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiResendEmailData = createMutationHook<
  EnrollmentMultiResendEmailSubmitInput,
  EnrollmentMultiVerifyEmailSubmitOutput
>(submitEnrollmentMultiResendEmailData)
